import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {
    ECEconomicsStrategyFeatures,
    ECEconomicsSubjectSummary,
    ECO_ERROR_PARAM_KEY,
    EconomicsErrorType,
    ECSurplusPremiumRateScope
} from 'smag-commons/dto/eco';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {Router} from '@angular/router';
import * as moment from 'moment-timezone';
import {
    ECJuridicalType,
    ECLegalForm,
    ECMember,
    ECMemberPersonalData,
    ECSubjectType,
    EnergyCommunity,
    EntityType
} from 'smag-commons/cfg/hierarchy';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {EconomicsError} from 'smag-commons/dto/eco/errors';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import * as _ from 'lodash';
import {Permission} from 'smag-commons/dto/api';
import {can} from '../../../shared/permissions';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import {isDefined} from '../../../commons/commons';
import {SmagApiService} from '../../../api/smag-api.service';
import {
    DEFAULT_DIALOG_MAX_WIDTH,
    DEFAULT_DIALOG_MIN_WIDTH,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_SIZES,
    isCommunityNotConfigured,
    isViewable,
    isWholeYearSelected,
    mkBillingPeriodParams,
    mkConsolidatedMonths, mkYearlyValue,
    sanityFromTimeActivationDate,
    sanityToTime,
    SortDirection,
    YearSummaryConsolidation
} from '../../../shared/ec-economics';
import {MatDialog} from '@angular/material/dialog';
import {ComputationStatusService} from '../../../shared/compute-status.service';
import {REDISTRIBUTION_SETTINGS, STRATEGY} from 'smag-commons/cfg/economics-values';
import {FormControl} from '@angular/forms';
import {ECMemberLegalFormTypePipe, ECMemberSubjectTypePipe} from '../../../shared/shared.pipe';
import {ECEconomicsFeatures} from 'smag-commons/dto/eco/community';


export interface SubjectMemberSummary {
    member: ECMember;
    subject: ECEconomicsSubjectSummary;
}

export interface SubjectSummaries {
    subjects: SubjectMemberSummary[];
    total: number;
    surplus_premium_rate_scope?: ECSurplusPremiumRateScope;
    surplus_premium_rate_total_private_tip?: number;
    surplus_premium_rate_maximum_private_tip?: number;
    surplus_premium_rate_exceeding_quota?: number;
    total_plants_incentive_revenue?: number;
    total_plants_incentive_cacv?: number;
    total_plants_incentive_tip?: number;
    total_plants_absorption_revenue?: number;
    total_plants_absorption_tip?: number;
    total_plants_absorption_cacv?: number;
    total_plants_absorption_rei?: number;
    total_relevant_consumption_energy?: number;
    total_relevant_production_energy?: number;
    total_relevant_energy?: number;
    total_saved_co2?: number;
    total_revenue?: number;
    errors?: EconomicsError[];
}

interface SummaryExceedingTotals {
    totalPrivateTip: number;
    maxPrivateTip: number;
    exceedingQuota: number;
    quotaScope: ECSurplusPremiumRateScope;
}

enum SortCriterion {
    PLANTS_INCENTIVE = 'PLANTS_INCENTIVE',
    ABSORPTION_REVENUE = 'ABSORPTION_REVENUE',
    ABSORPTION_EC_PLANTS_REI = 'ABSORPTION_EC_PLANTS_REI',
    RELEVANT_ENERGY = 'RELEVANT_ENERGY',
    CONTRIBUTION_REDUCTION_CO2 = 'SAVED_CO2',  // due to backend naming
    SUBJECT_REVENUE = 'SUBJECT_REVENUE',
}

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.PLANTS_INCENTIVE, SortDirection.DESC],
    [SortCriterion.ABSORPTION_REVENUE, SortDirection.DESC],
    // [SortCriterion.ABSORPTION_EC_PLANTS_REI, SortDirection.DESC],
    [SortCriterion.RELEVANT_ENERGY, SortDirection.DESC],
    [SortCriterion.CONTRIBUTION_REDUCTION_CO2, SortDirection.DESC],
    [SortCriterion.SUBJECT_REVENUE, SortDirection.DESC]
];

const DEFAULT_COLUMNS = ['subjects', 'plants_incentive', 'absorption_revenue', 'shared_energy_per_capita', 'contribution_reduction_co2', 'subject_revenue'];
const CACV_STRING = 'C<sub>ACV</sub>';
const SEARCH_DEBOUNCE_MILLIS = 400;

@Component({
    selector: 'app-ec-economics-subjects-summary',
    templateUrl: './ec-economics-subjects-summary.component.html',
    styleUrls: ['./ec-economics-subjects-summary.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Subjects per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class EcEconomicsSubjectsSummaryComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadSubjects: Subject<void> = new Subject<void>();
    private sortCriteria: [SortCriterion, SortDirection][] = [...DEFAULT_SORT_CRITERIA];
    private reloadEcoSubjectsData = new Subject<void>();
    private entity: HierarchyEntity;
    private subjectsErrors: EconomicsError[] = [];
    private searchSubj: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private subjectTypePipe: ECMemberSubjectTypePipe = new ECMemberSubjectTypePipe();
    private legalFormPipe: ECMemberLegalFormTypePipe = new ECMemberLegalFormTypePipe();
    private readonly filterSubject: Subject<any> = new BehaviorSubject(null);
    private activationDate: number = null;

    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    timeZone: string = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    subjects: BehaviorSubject<SubjectSummaries> = new BehaviorSubject<SubjectSummaries>(null);
    displayedColumns: string[] = DEFAULT_COLUMNS;
    futureEc = false;
    inactiveEc = false;
    loadingSubjects = true;
    communityNotConfigured = false;
    showComputationWarning = false;
    isWholeYear = false;
    tableTotals: ECEconomicsSubjectSummary;
    searchText = '';
    allTypesForFilter: (ECSubjectType | ECLegalForm)[] = [
        ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE,
        ECLegalForm.OTHER_INDIVIDUALS,
        ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY,
        ECSubjectType.PMI,
        ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW,
        ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS,
        ECSubjectType.CONDOMINIUM,
        ECSubjectType.OTHER,
    ];
    typeFilterControl: FormControl;
    exceedingTipTotals: SummaryExceedingTotals = null;
    summaryResponseSubject: BehaviorSubject<YearSummaryConsolidation[]> = new BehaviorSubject<YearSummaryConsolidation[]>([]);
    strategyFeatures: ECEconomicsStrategyFeatures = null;
    strategy: string = null;

    // Exported in HTML
    readonly SortCriterion = SortCriterion;
    readonly SortDirection = SortDirection;
    readonly CACV_STRING = CACV_STRING;
    readonly isDefined = isDefined;
    readonly isViewable = isViewable;
    readonly DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
    readonly DEFAULT_PAGE_SIZES = DEFAULT_PAGE_SIZES;
    readonly ECSurplusPremiumRateScope = ECSurplusPremiumRateScope;
    readonly ECEconomicsFeatures = ECEconomicsFeatures;

    constructor(private readonly router: Router,
                private readonly configurationService: ConfigurationService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly hierarchyService: HierarchyService,
                private readonly dialog: MatDialog,
                private readonly apiService: SmagApiService,
                private readonly computationService: ComputationStatusService,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.typeFilterControl = new FormControl(this.allTypesForFilter, []);
    }


    ngOnInit() {
        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.computationService.showComputingWarning
            .pipe(takeUntil(this.destroyed))
            .subscribe(bool => this.showComputationWarning = bool);

        // NOTE: as we store time range in URL, we need to avoid "navigation" to same entity when
        //       setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.hierarchyEntity.pipe(
            filter(he => he !== null),
            takeUntil(this.destroyed)
        ).subscribe(he => this.entity = he);

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) : of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.activationDate = (entity as EnergyCommunity)?.activation_date || null;
            this.billingPeriodCfg = {
                pastDate: this.activationDate,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });

        this.searchSubj.pipe(debounceTime(SEARCH_DEBOUNCE_MILLIS)).subscribe(this.filterSubject);
    }

    ngAfterViewInit() {
        this.billingPeriodSubj.pipe(takeUntil(this.destroyed))
            .subscribe(billingPeriod => this.billingPeriod = billingPeriod);
        let lastBillingPeriod = null;  // initialize for first iteration
        merge(this.paginator.page, this.reloadSubjects.pipe(tap(() => this.paginator.firstPage())))
            .pipe(startWith(undefined as unknown),
                switchMap(() =>
                    combineLatest([
                        this.hierarchyEntity,
                        this.billingPeriodSubj,
                        this.filterSubject,
                        this.typeFilterControl.valueChanges.pipe(startWith(null))
                    ])),
                switchMap(([hierarchyEntity, billingPeriod, filterChange, typeFilterChange]) => {
                    const billingPeriodChanged = lastBillingPeriod !== billingPeriod;
                    lastBillingPeriod = billingPeriod;  // update billing period
                    this.loadingSubjects = true;
                    const nameFilter = this.searchText;
                    const types: string[] = this.typeFilterControl.value || null;
                    return this.mkSubjects(
                        hierarchyEntity, billingPeriod.fromTime, billingPeriod.toTime,
                        this.paginator.pageIndex + 1, this.paginator.pageSize, nameFilter, types)
                        .pipe(
                            startWith(null as SubjectSummaries), // triggers spinner
                            catchError(err => {
                                this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving subjects'), err);
                                return of(null as SubjectSummaries);
                            }),
                            map(subjects => ({
                                subjects,
                                billingPeriodChanged
                            }))
                        );
                }),
                takeUntil(this.destroyed),
                switchMap(({subjects, billingPeriodChanged}) => combineLatest([
                    this.billingPeriodCfg.pastDate ?
                        mkConsolidatedMonths(this.entity.id, this.billingPeriodCfg.pastDate, this.timeZone, this.apiService) : of(null),
                    of(subjects),
                    of(billingPeriodChanged),
                    this.apiService.getEconomicsStrategyFeatures(this.entity.id, moment.tz(this.billingPeriod.fromTime, this.timeZone).year()),
                    this.apiService.getYearlyValue(mkYearlyValue(STRATEGY, null, this.entity.id, moment.tz(this.billingPeriod.fromTime, this.timeZone).year())),
                ]))
            ).subscribe(([consolidations, subjects, billingPeriodChanged, strategyFeatures, strategy]) => {
            this.strategyFeatures = strategyFeatures;
            this.strategy = strategy.value.strategy_type;
            this.summaryResponseSubject.next(consolidations);
            if (subjects) {
                this.subjectsErrors = subjects.errors ? subjects.errors : [];
                this.communityNotConfigured = isCommunityNotConfigured(subjects, this.strategy);
                this.subjects.next(subjects);
                this.tableTotals = this.mkTableTotals(subjects);
                if (billingPeriodChanged || !this.exceedingTipTotals) {  // only update exceeding tip section if billing period has been touched
                    this.exceedingTipTotals = {
                        totalPrivateTip: subjects.surplus_premium_rate_total_private_tip,
                        maxPrivateTip: subjects.surplus_premium_rate_maximum_private_tip,
                        exceedingQuota: subjects.surplus_premium_rate_exceeding_quota,
                        quotaScope: subjects.surplus_premium_rate_scope
                    };
                }
                this.loadingSubjects = false;
            }
        });

        this.changeDetectorRef.detectChanges();
    }

    // This function sums the totals for the current full filtered table and shows the results at the end of the html page
    mkTableTotals(s: SubjectSummaries): ECEconomicsSubjectSummary {
        return {
            subject_id: null,
            plants_tip: s.total_plants_incentive_tip,
            plants_cacv: s.total_plants_incentive_cacv,
            plants_incentive: s.total_plants_incentive_revenue,
            surplus_premium_rate_total_private_tip: s.surplus_premium_rate_total_private_tip,
            subject_revenue: s.total_revenue,
            absorption_tip: s.total_plants_absorption_tip,
            relevant_consumption_energy: s.total_relevant_consumption_energy,
            relevant_production_energy: s.total_relevant_production_energy,
            relevant_energy: s.total_relevant_energy,
            absorption_revenue: s.total_plants_absorption_revenue,
            absorption_cacv: s.total_plants_absorption_cacv,
            absorption_ec_plants_rei: s.total_plants_absorption_rei,
            relevant_consumption_ratio: null,
            relevant_production_ratio: null,
            saved_co2: s.total_saved_co2,
            gse: s.subjects.map(a => a.subject.gse).reduce((a, b) => a && b, true),
        } as ECEconomicsSubjectSummary;
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onClickSearchText() {
        const search = this.cleanSearchText;
        if (search) {
            this.paginator.firstPage();
        }
        this.searchSubj.next(search);
    }

    onClickClearSearchText() {
        this.searchText = '';
        this.paginator.firstPage();
        this.searchSubj.next(this.searchText);
    }

    resetFilters() {
        this.searchText = '';
        this.typeFilterControl.setValue(this.allTypesForFilter);
        this.sortCriteria = DEFAULT_SORT_CRITERIA;
        this.filterSubject.next(null);
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.isWholeYear = isWholeYearSelected(period.fromTime, period.toTime, this.activationDate, this.timeZone, period.type);
        this.router.navigate([], {queryParams: mkBillingPeriodParams(period), queryParamsHandling: 'merge'}).then();
    }

    onClickSortBy(criterion: SortCriterion, direction: SortDirection) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, direction],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        // reload subjects
        this.reloadSubjects.next();
    }

    getNameFor(personalData: ECMemberPersonalData): string {
        return personalData.company_name || (personalData.first_name && personalData.last_name ?
            personalData.first_name + ' ' + personalData.last_name : '-');
    }

    missingRedistributionParam(param = REDISTRIBUTION_SETTINGS) {
        return this.subjectsErrors.some((error: EconomicsError) =>
            error.type === EconomicsErrorType.MISSING_PARAM &&
            error.metadata[ECO_ERROR_PARAM_KEY] === param);
    }

    getIconAndColorBasedOnJuridicalData(juridicalType: ECJuridicalType, legalForm: ECLegalForm, subjectType: ECSubjectType): string[] {
        if (juridicalType === ECJuridicalType.INDIVIDUAL) {
            if (legalForm === ECLegalForm.ENTREPRENEUR_SOLE_PROPRIETORSHIP_AGRICULTURAL_ENTERPRISE) {
                return ['fal-briefcase', 'red-shade'];
            }
            if (legalForm === ECLegalForm.OTHER_INDIVIDUALS) {
                return ['fal-user', 'green-shade'];
            }
        }
        if (juridicalType === ECJuridicalType.CORPORATE_ENTITY) {
            switch (subjectType) {
                case ECSubjectType.TERRITORIAL_BODY_LOCAL_AUTHORITY_OTHER_BODY:
                    return ['fal-landmark', 'yellow-shade'];
                case ECSubjectType.PMI:
                    return ['fal-industry-alt', 'red-shade'];
                case ECSubjectType.ASSOCIATION_WITH_LEGAL_PERSONALITY_UNDER_PRIVATE_LAW:
                    return ['fal-balance-scale', 'purple-shade'];
                case ECSubjectType.ASSOCIATED_FIRM_COMPANY_OF_PROFESSIONALS:
                    return ['fal-pen-nib', 'red-shade'];
                case ECSubjectType.OTHER:
                    return ['fal-file-signature', 'light-blue-shade'];
                default:
                    return ['fal-home', 'green-shade'];
            }
        }
        return null;  // default, should not happen
    }

    getTitleBasedOnJuridicalData(subjectType: ECSubjectType, legalForm: ECLegalForm): string {
        if (isDefined(subjectType)) {
            return this.subjectTypePipe.transform(subjectType);
        }
        if (isDefined(legalForm)) {
            return this.legalFormPipe.transform(legalForm);
        }
        return null;
    }

    get showNullData() {
        return this.inactiveEc || this.futureEc;
    }

    get cleanSearchText() {
        return this.searchText.trim();
    }

    private showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            minWidth: `${DEFAULT_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    private mkSubjects(entity: HierarchyEntity, fromTime: number, toTime: number, page: number,
                       size: number, nameFiltering: string, typesFiltering: string[]): Observable<SubjectSummaries> {
        const orderBy: string[] = this.sortCriteria.map(([criterion, direction]) => `${criterion}_${direction}`);
        // Pick lowest fromTime from (fromTime, activationDate)
        fromTime = sanityFromTimeActivationDate(fromTime, this.billingPeriodCfg.pastDate, this.timeZone);
        // Pick lowest toTime from (toTime, currentTime)
        toTime = sanityToTime(this.timeZone, toTime);
        if (entity?.type === EntityType.ENERGY_COMMUNITY && isViewable(entity) && (!this.inactiveEc && !this.futureEc)) {
            this.reloadEcoSubjectsData.next();
            return this.computationService.checkComputationStatus(entity.id, moment.tz(this.billingPeriod.fromTime, this.timeZone).startOf('month').year())
                .pipe(takeUntil(merge(this.destroyed, this.reloadEcoSubjectsData)),
                    switchMap(() => {
                        return this.apiService.ecEconomicsSubjectsSummaries(entity.id, fromTime, toTime, page, size, nameFiltering, orderBy, typesFiltering)
                            .pipe(switchMap(resp => resp.subjects.total ?
                                forkJoin(resp.subjects.results.map(summarySubject => (summarySubject.subject_id ?
                                    this.apiService.loadMember(summarySubject.subject_id) :
                                    of(null as ECMember))
                                    .pipe(map(member => [summarySubject, member] as [ECEconomicsSubjectSummary, ECMember]))
                                )).pipe(map(subj => ({
                                    subjects: mkSubject(subj),
                                    total: resp.subjects.total,
                                    errors: resp.errors,
                                    surplus_premium_rate_scope: resp.surplus_premium_rate_scope,
                                    surplus_premium_rate_total_private_tip: resp.surplus_premium_rate_total_private_tip,
                                    surplus_premium_rate_maximum_private_tip: resp.surplus_premium_rate_maximum_private_tip,
                                    surplus_premium_rate_exceeding_quota: resp.surplus_premium_rate_exceeding_quota,
                                    total_plants_incentive_revenue: resp.total_plants_incentive_revenue,
                                    total_plants_incentive_cacv: resp.total_plants_incentive_cacv,
                                    total_plants_incentive_tip: resp.total_plants_incentive_tip,
                                    total_plants_absorption_revenue: resp.total_plants_absorption_revenue,
                                    total_plants_absorption_tip: resp.total_plants_absorption_tip,
                                    total_plants_absorption_cacv: resp.total_plants_absorption_cacv,
                                    total_plants_absorption_rei: resp.total_plants_absorption_rei,
                                    total_relevant_consumption_energy: resp.total_relevant_consumption_energy,
                                    total_relevant_production_energy: resp.total_relevant_production_energy,
                                    total_relevant_energy: resp.total_relevant_energy,
                                    total_saved_co2: resp.total_saved_co2,
                                    total_revenue: resp.total_revenue
                                }))) :
                                of({subjects: [], total: 0, errors: resp.errors})
                            ));
                    }));
        } else {
            return of({subjects: [], total: 0, errors: []});
        }
    }

}

function mkSubject(subjects: [ECEconomicsSubjectSummary, ECMember][]): SubjectMemberSummary[] {
    return _.flatten(subjects.map(([subject, ecMember]) => [
        {member: ecMember, subject} as SubjectMemberSummary]));
}

